import type { PersonListRow, PersonRecord } from "../../../server/core/people";
import type { ScreenContext } from "../../shell/types";
import { api, el } from "../../ui/dom";
import { icon } from "../../ui/icons";
import { firstPageParams, list, type Column } from "../../ui/list";
import type { PageInfo } from "../../../server/rules/list-page";
import { openModal } from "../../ui/modal";
import { chip } from "../../ui/record";
import { field, refuse, select } from "../clients/forms";
import { pageHead } from "../empty";
import { personRecord } from "./record";

// Home Care › Caregivers and Transportation › Drivers (brief 1B-2, mockup homecare/caregivers and
// transport/drivers): the real people with their Board title, a Ready / Not ready chip and what's
// missing, a filter by title, and Add caregiver (or driver) in a modal. A row opens the person.

export type PeopleScreen = "caregivers" | "drivers";
const ROLE = { caregivers: "caregiver", drivers: "driver" } as const;
const NOUN = { caregivers: "caregiver", drivers: "driver" } as const;

/** The line under each list's title (mockup, with Staffing on or off). */
function line(screen: PeopleScreen, staffingOn: boolean): string {
  if (screen === "drivers")
    return "Credentialed with the drivers' requirements: driver's license, driving record, background check and drug screen.";
  return staffingOn
    ? "Hired and credentialed through Staffing. Home Care posts requisitions like any other client."
    : "Staffing isn't on, so Home Care hires its own caregivers here with the same requirements library.";
}

/** Ready, Not ready, or not worked out yet. */
export function readyChip(ready: boolean | null): HTMLElement {
  if (ready === null) return chip("Not checked yet");
  return ready ? chip("Ready", "ok") : chip("Not ready", "bad");
}

export async function peopleScreen(host: HTMLElement, ctx: ScreenContext) {
  const screen = ctx.screen.id as PeopleScreen;
  if (!(screen in ROLE)) return;
  if (ctx.record) return personRecord(host, ctx, screen, ctx.record);
  return peopleList(host, ctx, screen);
}

async function peopleList(host: HTMLElement, ctx: ScreenContext, screen: PeopleScreen) {
  type Answer = PageInfo & {
    people: PersonListRow[];
    titles: { code: string; name: string }[];
    canEdit: boolean;
  };
  // Brief 1E-32: the server answers one page at a time.
  const ask = (params: URLSearchParams) =>
    api<Answer>("GET", `/api/people?role=${ROLE[screen]}&${params.toString()}`);
  const listId = `${ctx.section.id}/${screen}`;
  const res = await ask(firstPageParams(listId, ctx.me.user.id));
  const add = el(
    "button",
    { type: "button", class: "btn pri" },
    icon("person-add"),
    ` Add ${NOUN[screen]}`,
  );
  add.addEventListener("click", () => newPerson(ctx, screen, res.ok ? res.body.titles : []));
  host.append(
    pageHead(
      ctx,
      line(screen, ctx.me.modulesOn.includes("staffing")),
      res.ok && res.body.canEdit ? [add] : [],
    ),
  );
  if (!res.ok) {
    host.append(el("p", { class: "note", role: "alert" }, `The ${screen} couldn't be loaded.`));
    return;
  }
  const readyText = (r: PersonListRow) =>
    r.ready === null ? "Not checked yet" : r.ready ? "Ready" : "Not ready";
  const columns: Column<PersonListRow>[] = [
    {
      id: "name",
      label: screen === "drivers" ? "Driver" : "Caregiver",
      text: (r) => r.name,
      search: true,
      cell: (r) =>
        el(
          "span",
          {},
          el("b", {}, r.name),
          r.status !== "active"
            ? el("span", {}, " ", chip(r.statusLabel, r.status === "do_not_use" ? "bad" : "plain"))
            : null,
        ),
    },
    { id: "title", label: "Title", text: (r) => r.title ?? "No title", search: true },
    { id: "mobile", label: "Mobile", text: (r) => r.mobile ?? "–", search: true },
    {
      id: "ready",
      label: "Readiness",
      text: readyText,
      cell: (r) => readyChip(r.ready),
      values: ["Ready", "Not ready"],
    },
    {
      id: "missing",
      label: "What's missing",
      text: (r) => r.missing.join(", ") || "Nothing",
      cell: (r) =>
        r.missing.length
          ? el(
              "span",
              { class: "small" },
              r.missing.slice(0, 3).join(" · "),
              r.missing.length > 3 ? ` +${r.missing.length - 3}` : "",
            )
          : el("span", { class: "muted small" }, "Nothing"),
    },
  ];
  host.append(
    list<PersonListRow>({
      id: listId,
      first: { rows: res.body.people, page: res.body },
      userId: ctx.me.user.id,
      noun: screen,
      source: async (params) => {
        const r = await ask(params);
        return r.ok ? { rows: r.body.people, page: r.body } : null;
      },
      rowId: (r) => r.id,
      columns,
      chips: { values: ["Ready", "Not ready"], of: readyText },
      onOpen: (r) => ctx.go(`/${ctx.section.id}/${screen}/${r.id}`),
      empty: `No ${screen} yet.`,
    }),
  );
}

/** Add caregiver (or driver), in a modal: name, mobile, email, title and license. */
function newPerson(
  ctx: ScreenContext,
  screen: PeopleScreen,
  titles: readonly { code: string; name: string }[],
) {
  const first = field("First name", "", { maxlength: "60", autocomplete: "given-name" });
  const last = field("Last name", "", { maxlength: "60", autocomplete: "family-name" });
  const mobile = field("Mobile", "", { type: "tel", maxlength: "20", inputmode: "tel" });
  const email = field("Email", "", { type: "email", maxlength: "160" });
  const title = select(
    "Title",
    [["", "No title"], ...titles.map((t) => [t.code, `${t.code} · ${t.name}`] as const)],
    "",
  );
  const state = field("License state", "MD", { maxlength: "2" });
  const number = field("License or certificate number", "", { maxlength: "30" });
  const create = el(
    "button",
    { type: "submit", class: "btn pri" },
    icon("plus"),
    ` Add ${NOUN[screen]}`,
  );
  const form = el(
    "form",
    { class: "form", style: "max-width:none", novalidate: "" },
    el(
      "div",
      { class: "formgrid" },
      first.el,
      last.el,
      mobile.el,
      email.el,
      title.el,
      el("div"),
      state.el,
      number.el,
      el(
        "p",
        { class: "hint wide", style: "margin:-4px 0 0" },
        "Maryland Board of Nursing titles only. An old CNA/GNA certificate is CNA-I; an old CNA is CNA-II, with the same number.",
      ),
    ),
  );
  const m = openModal({
    title: `Add ${NOUN[screen]}`,
    sub: "Their documents and readiness come next, on their record.",
    body: [form],
    actions: [create],
    wide: true,
  });
  first.input.focus();
  const submit = async () => {
    create.disabled = true;
    const res = await api<PersonRecord>("POST", "/api/people", {
      firstName: first.input.value,
      lastName: last.input.value,
      mobile: mobile.input.value || null,
      email: email.input.value || null,
      roles: [ROLE[screen]],
      title: title.input.value || null,
      licenseState: title.input.value ? state.input.value || null : null,
      licenseNumber: title.input.value ? number.input.value || null : null,
    });
    create.disabled = false;
    if (!res.ok) return refuse(m.note, res.body.error, openOf(res.body));
    m.close();
    ctx.go(`/${ctx.section.id}/${screen}/${res.body.person.id}`);
  };
  form.addEventListener("submit", (e) => {
    e.preventDefault();
    void submit();
  });
  create.addEventListener("click", (e) => {
    e.preventDefault();
    void submit();
  });
}

/** The record a refusal names ("Already on Ana Ruiz's record." with Open), if any. */
export const openOf = (b: unknown) => {
  const o = b as { open?: unknown; openLabel?: unknown };
  return typeof o?.open === "string"
    ? { href: o.open, label: typeof o.openLabel === "string" ? o.openLabel : "Open" }
    : null;
};
